import type { BileliUser } from '../../../util/bileli/types';

import { BILELI_API_BASE, BileliApiError, bileliApiJson } from '../../../util/bileli/bileliApi';
import { pause } from '../../../util/schedulers';
import { callApi } from '../../../api/gramjs';
import { ensureProfileNamed, setSessionUser } from '../../../bileli/modules/account';
import { clearBileliStores } from '../../../bileli/shared/auth/clearBileliStores';
import {
  forgetAuthedTelegramUid, getAuthedTelegramUid, parseTelegramUidFromInitData,
  rememberAuthedTelegramUid, shouldReauthForIdentity,
} from '../../../bileli/shared/auth/identity';
import { getGlobal, setGlobal } from '../../index';
import { updateBileliAuth } from '../../reducers/bileli';

// Bileli: the bot whose token the backend verifies. why: minting THIS bot's Mini-App initData is
// what cryptographically proves "this Telegram user is our user" to /auth/telegram/verify.
//
// Environment-aware since docs/backend/TASK-web-dev-signin-bot.md: dev and prod shared one bot, so
// developing locally required the PRODUCTION bot token in the backend's .env — and that token is the
// HMAC key the verify endpoint checks, so whoever holds it can mint a valid initData for ANY Telegram
// user id and get a session as any production user. Dev now signs with `bileli_auth_dev_bot`
// (BILELI_AUTH_BOT in .env.development → TG_BILELI_AUTH_BOT via vite.config).
//
// The prod bot stays the LITERAL fallback on purpose: the failure mode of a missing var must be
// "behaves exactly like today", never "silently signs against nothing".
const BILELI_AUTH_BOT_USERNAME = import.meta.env.TG_BILELI_AUTH_BOT || 'bileli_auth_bot';

// Bileli: silent "Sign in with Telegram → Bileli". why: the fork IS the Bileli education app, so
// per-user teacher/student data needs a Bileli session (httpOnly cookies) that is SEPARATE from the
// Telegram MTProto session. We mint a bot-signed `initData` from the user's existing MTProto session
// (RequestMainWebView against @bileli_auth_bot), POST it to /auth/telegram/verify (which sets the
// cookies), and confirm with /me. No UI, no OTP, no second sign-in — identical handshake to native
// iOS. Full recipe: docs/backend/telegram-auth-web-integration.md.
export async function runBileliWebAuth(): Promise<void> {
  // Bileli: yield off the current dispatch FIRST. why: this is invoked from inside a global updater
  // (initial.ts authorizationStateReady seam), which still holds its own captured `global`; a synchronous
  // setGlobal here (isAuthing below) would make that copy stale and trip dev's strict
  // "[TeactN.setGlobal] Attempt to set an outdated global" check — froze fresh QR+2FA logins (reloads were
  // fine only because wasAuthReady short-circuits). After the pause we re-read getGlobal() fresh, and the
  // guard→setGlobal window below contains no await, so any interleaving stays safe.
  await pause(0);
  let global = getGlobal();
  if (global.bileliAuth?.isAuthed || global.bileliAuth?.isAuthing) return; // once per session

  // Bileli: intentionally NOT gated on global.currentUserId. why: at authorizationStateReady the
  // MTProto session is already authorized (all requestMainWebView needs), but the Telegram user
  // object can still be loading (GetFullUser resolves later) — gating on it silently no-op'd the
  // whole handshake on a fresh QR/2FA login.
  global = updateBileliAuth(global, { isAuthing: true, error: undefined, errorStatus: undefined });
  setGlobal(global);

  try {
    // Bileli: SESSION-FIRST — reuse the httpOnly cookie session before minting a new one. why: every
    // accepted /auth/telegram/verify counts toward the backend's sliding window (5 sign-ins / 10 min
    // per Telegram user — TelegramAuthRateLimiter.kt), so verifying on every reload rate-limited real
    // users (429 after 5 reloads). GET /me rides the cookies (with the client's single-flight
    // 401→refresh→retry) and costs nothing against the window; only a truly dead session falls
    // through to the verify handshake below.
    const existing = await fetchSessionUser();
    if (existing) {
      global = getGlobal();
      global = updateBileliAuth(global, { isAuthing: false, isAuthed: true, user: existing });
      setGlobal(global);
      // eslint-disable-next-line no-console
      console.info('[Bileli] session reused for', existing.displayName ?? existing.id);
      return;
    }

    let initData = await mintInitData();
    let outcome = await exchangeInitData(initData);
    if (!outcome.ok && outcome.status === 401) {
      // Bileli: a 401 is usually a stale auth_date (>~10 min) — re-mint once + retry (doc §7).
      // ONLY on 401: retrying a 429/5xx/network failure can't succeed and doubles the traffic.
      initData = await mintInitData();
      outcome = await exchangeInitData(initData);
    }
    if (!outcome.ok) {
      throw new BileliApiError(outcome.detail ?? 'verify rejected', outcome.status ?? 0, outcome.detail);
    }

    // Bileli: record WHICH Telegram account this fresh Bileli session belongs to — the id lives inside the
    // initData we just verified (a copy; the verified bytes are untouched). This is the only path that
    // creates a session tied to a known Telegram user, so it's the only place we can trust to record it.
    // reconcileBileliIdentity later compares it against the live Telegram user to catch a stale cookie.
    const mintedUid = parseTelegramUidFromInitData(initData);
    if (mintedUid) rememberAuthedTelegramUid(mintedUid);

    let user = await bileliApiJson<BileliUser>('/me');
    // Bileli: name the personal org from Telegram when it's still the default "Personal" — that org name
    // (NOT displayName, which Telegram signup always sets) is the backend gate that blocks publishing.
    // Best-effort + self-contained; a pure-emoji/blank Telegram name is left for the manual entry at publish.
    user = await ensureProfileNamed(user);
    global = getGlobal();
    global = updateBileliAuth(global, { isAuthing: false, isAuthed: true, user });
    setGlobal(global);
    // eslint-disable-next-line no-console
    console.info('[Bileli] signed in as', user.displayName ?? user.id);
  } catch (err) {
    // Bileli: keep the HTTP status so the UI can explain the failure (429 gets the rate-limit copy
    // instead of the misleading eternal «Входим в Bileli…»).
    global = getGlobal();
    global = updateBileliAuth(global, {
      isAuthing: false,
      isAuthed: false,
      error: err instanceof Error ? err.message : String(err),
      errorStatus: err instanceof BileliApiError ? err.status : undefined,
    });
    setGlobal(global);
    // eslint-disable-next-line no-console
    console.warn('[Bileli] Telegram→Bileli sign-in failed:', err);
  }
}

// Bileli: END the Bileli session — the teardown Telegram's own signOut never did. why: the reported bug
// is that logging out then logging in with a DIFFERENT phone showed the OLD account. Root cause: the
// bileli_access/bileli_refresh cookies are httpOnly (only the backend can expire them), and nothing ever
// called POST /auth/logout — so the cookie outlived the logout and the next GET /me answered as the old
// user. This revokes it server-side, drops the identity marker, resets the auth slice, and wipes the
// account-scoped stores. Best-effort + keepalive so it still lands if signOut triggers a reload.
export async function clearBileliSession(): Promise<void> {
  try {
    await fetch(`${BILELI_API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true, // survive a page unload that signOut may trigger right after
      headers: { Accept: 'application/json' },
    });
  } catch { /* network/offline — the cookie may linger, but the identity check below still re-auths */ }
  forgetAuthedTelegramUid();
  clearBileliStores();
  let global = getGlobal();
  global = updateBileliAuth(global, {
    isAuthing: false, isAuthed: false, user: undefined, error: undefined, errorStatus: undefined,
  });
  setGlobal(global);
}

// Bileli: catch a stale Bileli session belonging to a DIFFERENT Telegram account and re-auth as the live
// one. why: the session-first path (above) trusts any valid cookie, and GET /me carries no Telegram id to
// compare — so an account switch (or a logout whose revoke failed) silently kept the old user. Called from
// the onUpdateCurrentUser seam, the one reliable moment the live Telegram uid is known.
let reconciling = false;
export async function reconcileBileliIdentity(liveUid: string): Promise<void> {
  if (reconciling) return;
  if (!shouldReauthForIdentity(getAuthedTelegramUid(), liveUid)) return;
  reconciling = true;
  try {
    await clearBileliSession(); // revoke the old cookie + reset auth so runBileliWebAuth isn't short-circuited
    await runBileliWebAuth(); // no cookie now ⇒ mint+verify as the live Telegram user, recording its uid
  } finally {
    reconciling = false;
  }
}

// Bileli: probe the existing cookie session. why: distinguishes "no/expired session" (→ run the verify
// handshake) from transient states without throwing into the caller's happy path.
async function fetchSessionUser(): Promise<BileliUser | undefined> {
  try {
    return await bileliApiJson<BileliUser>('/me');
  } catch {
    return undefined;
  }
}

// Bileli: instant profile re-sync. why: the backend mirrors Telegram profile fields (username/name/photo)
// ONLY inside POST /auth/telegram/verify (feat/telegram-profile-sync) — a plain GET /me keeps serving the
// stale copy. When the user just set an @username in the fork's own settings, re-running the silent
// handshake re-mints initData (which now carries the username), the backend re-mirrors it, and /me returns
// it — no backend changes, no re-login. Re-verify rotates the httpOnly cookies for the SAME user (harmless).
let resyncPromise: Promise<void> | undefined; // single-flight: two home screens may mount the nudge
let resyncedFor: string | undefined; // username value already confirmed mirrored — never re-verify for it

export function resyncBileliProfile(tgUsername: string): Promise<void> {
  if (resyncedFor === tgUsername) return Promise.resolve();
  if (resyncPromise) return resyncPromise;
  resyncPromise = (async () => {
    // Needs a live Bileli session — the sign-in handshake itself mirrors, so there's nothing to do here.
    const global = getGlobal();
    if (!global.bileliAuth?.isAuthed) return;
    let outcome = await exchangeInitData(await mintInitData());
    if (!outcome.ok && outcome.status === 401) {
      outcome = await exchangeInitData(await mintInitData()); // one retry: stale auth_date only (doc §7)
    }
    if (!outcome.ok) throw new Error('re-verify rejected');
    const user = await bileliApiJson<BileliUser>('/me');
    setSessionUser(user);
    if (user.telegramUsername) {
      resyncedFor = tgUsername; // success = the mirror actually filled; failures may retry on a later mount
    }
  })().finally(() => {
    resyncPromise = undefined;
  });
  return resyncPromise;
}

// Bileli: resolve @bileli_auth_bot, then ask Telegram for its Main Mini App URL — the bot-signed
// initData rides in the URL fragment as `tgWebAppData`. why: a standalone MTProto client can obtain
// a valid bot-signed initData this way without ever opening a Mini-App host (the URL is never loaded).
async function mintInitData(): Promise<string> {
  const resolved = await callApi('getChatByUsername', BILELI_AUTH_BOT_USERNAME);
  const bot = resolved?.user;
  // Name the bot we ACTUALLY asked for: with dev and prod on different bots, a hardcoded name here
  // sends whoever reads the error looking at the wrong one.
  if (!bot) throw new Error(`@${BILELI_AUTH_BOT_USERNAME} not resolvable`);

  const webView = await callApi('requestMainWebView', { bot, peer: bot });
  const url = webView?.url;
  if (!url) throw new Error('no web view url (is the bot Main Mini App enabled?)');

  const fragment = url.split('#')[1] ?? '';
  const initData = new URLSearchParams(fragment).get('tgWebAppData');
  if (!initData) throw new Error('no tgWebAppData in web view url');
  // Bileli: return VERBATIM. why: any extra decode / re-encode / JSON.parse(user) changes the bytes
  // and breaks the backend HMAC check → 401 (doc §2).
  return initData;
}

type VerifyOutcome = { ok: true } | { ok: false; status?: number; detail?: string };

// Bileli: exchange initData for httpOnly cookies. why: web auth is cookies-only — we read NOTHING of
// the tokens from the JSON body (its accessToken/refreshToken exist only for the native Bearer
// transport). On failure the outcome carries the HTTP status + the RFC-7807 `detail` so the caller can
// retry only what's retryable (401 = stale auth_date) and show the real reason (429 = rate limit).
async function exchangeInitData(initData: string): Promise<VerifyOutcome> {
  try {
    const res = await fetch(`${BILELI_API_BASE}/auth/telegram/verify`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ initData }),
    });
    if (res.ok) return { ok: true };
    let detail: string | undefined;
    try {
      const parsed = JSON.parse(await res.text());
      if (parsed && typeof parsed.detail === 'string') detail = parsed.detail;
    } catch { /* not JSON — keep only the status */ }
    return { ok: false, status: res.status, detail };
  } catch {
    // Bileli: network/CORS failure (e.g. our origin not yet on the backend CORS allow-list) — treat
    // as not-authed so the app keeps working in public/anonymous mode.
    return { ok: false };
  }
}
